import React, {
  useRef,
  useState,
  useEffect,
  createRef,
  useContext,
  useCallback,
} from 'react';
import chroma from 'chroma-js';
import classNames from 'classnames';
import intl from 'react-intl-universal';
import { createPortal } from 'react-dom';

import { IOneTag, ITags } from './Tags.d';
import usePortalDom from '../hooks/usePortalDom';
import { ThemeContext } from '../../store/ThemeProvider';
import { PageContext } from '../ComponentNavigatorV2/PageContext';

import './Tags.scss';

const OneShowMoreTag = (props: IOneTag) => {
  const { mainColor, h1Color } = useContext(ThemeContext);
  return (
    <div
      className={`${classNames('one-show-more-type', {
        active: props.isActive,
      })}`}
      style={{
        color: props.isActive ? h1Color : chroma(h1Color).alpha(0.6).css(),
      }}
      onClick={() => props.clickTag(props.id)}
    >
      {props.name}

      <div className="border">
        <span style={{ backgroundColor: mainColor }} />
      </div>
    </div>
  );
};

const OneTag = (props: IOneTag) => {
  const { mainColor, h1Color } = useContext(ThemeContext);

  return (
    <div
      className={`${classNames('one-type', {
        active: props.isActive,
        sp: props.sp,
      })}`}
      style={{
        color: props.isActive ? h1Color : chroma(h1Color).alpha(0.6).css(),
      }}
      onClick={() => props.clickTag(props.id)}
    >
      {props.name}

      <div className="border">
        <span style={{ backgroundColor: mainColor }} />
      </div>
    </div>
  );
};

let lastTagTypeScrollLeft = 0;

const Tags = React.forwardRef<any, ITags>((props, ref) => {
  const { showall, showperson, showhot } = props;
  // console.log(showall, showperson, showhot);
  const defaultTags = [
    showall !== false
      ? {
          name: intl.get('all'),
          id: 0,
        }
      : null,
    !!+window.configDetail.use_face &&
    !props.disableSystem &&
    showperson !== false
      ? {
          name: intl.get('Person'),
          id: -1,
        }
      : null,
    !props.disableSystem && showhot !== false
      ? {
          name: intl.get('Hot'),
          id: -2,
        }
      : null,
  ].filter(Boolean) as IOneTag[];

  useEffect(() => {
    // 判断应该显示哪个tag
    let targetId = 0;
    if (defaultTags.length > 0) {
      // 有系统的情况下
      targetId = defaultTags[0].id;
    } else {
      // 无系统的情况下
      // @ts-ignore
      let tags = [].concat(props.tags).filter(Boolean);
      if (tags && tags.length) {
        // @ts-ignore
        targetId = tags[0].id;
      }
    }

    if (Number(targetId) != Number(props.id)) {
      props.onUpdateTagId(Number(targetId));
    }
  }, [defaultTags.length]);

  const [showMore, setShowMore] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const arrowClass = classNames('a-r', { more: showMore });
  const renderDefaultTags = () =>
    defaultTags.map((tag: IOneTag) => {
      return (
        <OneTag
          name={tag.name}
          id={tag.id}
          key={tag.id}
          isActive={tag.id === props.id}
          clickTag={(id) => {
            props.clickTag(id);
            setShowMore(false);
          }}
          sp
        />
      );
    });
  const renderNormalTags = () => {
    // @ts-ignore
    let tags = [].concat(props.tags).filter(Boolean);
    // tags.sort((a: any, b: any) => {
    //   if (b.id == props.id) {
    //     return 1;
    //   }
    //   return 0;
    // });

    return tags.map((tag: any) => {
      return (
        <OneTag
          name={tag.name}
          id={tag.id}
          key={tag.name + tag.id}
          isActive={tag.id === props.id}
          clickTag={(id) => {
            props.clickTag(id);
            setShowMore(false);
          }}
        />
      );
    });
  };
  const renderShowMoreTags = () => {
    const tags = [...defaultTags, ...props.tags];
    return tags.map((tag) => (
      <OneShowMoreTag
        key={tag.id}
        clickTag={(id) => {
          props.clickTag(id);
          setShowMore(false);
        }}
        isActive={tag.id === props.id}
        name={tag.name}
        id={tag.id}
      />
    ));
  };
  const { componentColor, backgroundColor, mainColor, h1Color } =
    useContext(ThemeContext);
  const pageContext = useContext(PageContext);

  const anchorRef = useRef<HTMLElement | null>(null);

  const isElementVisible = (element: HTMLElement | null) => {
    if (!element) {
      return true;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return false;
    }
    let current: HTMLElement | null = element;
    while (current) {
      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0'
      ) {
        return false;
      }
      current = current.parentElement;
    }
    return true;
  };

  const updateVisibility = useCallback(() => {
    setShouldRender(isElementVisible(anchorRef.current));
  }, []);

  const assignAnchorRef = useCallback(
    (node: HTMLElement | null) => {
      anchorRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && 'current' in ref) {
        // @ts-ignore
        ref.current = node;
      }
      updateVisibility();
    },
    [ref, updateVisibility]
  );

  useEffect(() => {
    updateVisibility();
  }, [props.isFixed, updateVisibility]);

  const prevRenderRef = useRef(shouldRender);
  useEffect(() => {
    if (props.isFixed && shouldRender && !prevRenderRef.current) {
      window.dispatchEvent(new Event(`page-scroll-move-${pageContext.cid}`));
    }
    prevRenderRef.current = shouldRender;
  }, [props.isFixed, shouldRender, pageContext.cid]);

  useEffect(() => {
    const rerenderByVisibility = () => updateVisibility();
    window.addEventListener('SWIPER_INDEX_CHANGE', rerenderByVisibility);
    window.addEventListener('tab-index-change', rerenderByVisibility);
    window.addEventListener('custom-tab-index', rerenderByVisibility);
    window.addEventListener('resize', rerenderByVisibility);

    return () => {
      window.removeEventListener('SWIPER_INDEX_CHANGE', rerenderByVisibility);
      window.removeEventListener('tab-index-change', rerenderByVisibility);
      window.removeEventListener('custom-tab-index', rerenderByVisibility);
      window.removeEventListener('resize', rerenderByVisibility);
    };
  }, [updateVisibility]);

  const typeListRef = createRef<HTMLDivElement>();
  // type list 阻止垂直方向滚动
  useEffect(() => {
    const sp = function (e: TouchEvent) {
      //e.preventDefault();
      e.stopPropagation();
    };
    try {
      if (typeListRef.current) {
        typeListRef.current.addEventListener('touchstart', sp, {
          passive: false,
        });
      }
    } catch (e) {}
    return () => {
      try {
        if (typeListRef.current) {
          typeListRef.current.removeEventListener('touchstart', sp);
        }
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    try {
      if (
        typeListRef &&
        typeListRef.current &&
        typeof typeListRef.current.scrollTo == 'function'
      ) {
        typeListRef.current.scrollTo(lastTagTypeScrollLeft, 0);
      }
    } catch (e) {}
  }, [!!props.isFixed && shouldRender, typeListRef]);

  const portal = usePortalDom({
    targetClassName: 'fixed-top-wrapper',
    parentElement: window.document.getElementById(
      `page-top-${pageContext.cid}`
    )! as HTMLDivElement,
  });

  useEffect(() => {
    if (props.isFixed && !shouldRender && portal) {
      portal.innerHTML = '';
    }
  }, [props.isFixed, shouldRender, portal]);

  const tagsContent = (
    <div
      ref={props.isFixed ? undefined : assignAnchorRef}
      className={classNames('type-list-wrapper', { fixed: props.isFixed })}
    >
      <div
        className={classNames('type-list-tag-row')}
        style={{
          backgroundColor: props.isFixed
            ? chroma(componentColor).alpha(1).css()
            : componentColor,
        }}
      >
        <div
          className="type-list"
          ref={typeListRef}
          onScroll={(e: any) => {
            if (e && e.currentTarget) {
              lastTagTypeScrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          {showMore ? (
            <span className={'one-type sp active'} style={{ color: h1Color }}>
              {intl.get('Select')}
            </span>
          ) : (
            <>
              {renderDefaultTags()}
              {renderNormalTags()}
            </>
          )}
        </div>
        <div
          className={classNames('type-list-more-wrapper')}
          onClick={() => setShowMore(!showMore)}
        >
          <img
            src={require('../../assets/arrow-right.png')}
            alt=""
            className={arrowClass}
          />
        </div>
      </div>
      {showMore && (
        <div
          className={classNames('type-list-wrapper-more')}
          style={{
            backgroundColor: props.isFixed
              ? chroma(componentColor).alpha(1).css()
              : componentColor,
            borderTopColor: chroma(h1Color).alpha(0.1).css(),
          }}
        >
          {renderShowMoreTags()}
        </div>
      )}
    </div>
  );

  return (
    <>
      {props.isFixed ? (
        <div ref={assignAnchorRef} style={{ height: 40 }} />
      ) : null}
      {props.isFixed
        ? portal
          ? shouldRender
            ? createPortal(tagsContent, portal)
            : null
          : null
        : tagsContent}
    </>
  );
});

export default Tags;
